import { useMemo } from "react";
import {
  calcularEstadoSuscripcion,
  diasRestantes,
  agruparPorEstado,
  calcularDineroSuscripciones,
  calcularTasaRenovacion,
} from "./utils";

/**
 * Hook que consume el store de CoreBase y retorna métricas de suscripciones.
 * Solo devuelve datos útiles si hay ventas con productos recurrentes.
 */
export function useSubscriptionMetrics(store) {
  const ventasRecurrentes = useMemo(
    () => store.ventas.filter(v => !v.anulada && v.endDate),
    [store.ventas]
  );

  const hayRecurrentes = ventasRecurrentes.length > 0;

  const grupos = useMemo(
    () => (hayRecurrentes ? agruparPorEstado(store.ventas) : { activa: [], por_vencer: [], vencida: [] }),
    [store.ventas, hayRecurrentes]
  );

  const dinero = useMemo(
    () => (hayRecurrentes ? calcularDineroSuscripciones(store.ventas) : null),
    [store.ventas, hayRecurrentes]
  );

  const tasaRenovacion = useMemo(
    () => (hayRecurrentes ? calcularTasaRenovacion(store.ventas) : 0),
    [store.ventas, hayRecurrentes]
  );

  // Alertas de suscripción (para integrar con alertas existentes)
  const alertasSuscripcion = useMemo(() => {
    if (!hayRecurrentes) return [];
    const arr = [];
    grupos.por_vencer.forEach(v => {
      const dias = diasRestantes(v);
      arr.push({
        id: `sub-vence:${v.id}`,
        tipo: "warning",
        mensaje: `Suscripción de ${v.nombreCliente} vence en ${dias} día${dias !== 1 ? "s" : ""} (${v.productoNombre})`,
        ventaId: v.id,
      });
    });
    grupos.vencida.forEach(v => {
      const dias = Math.abs(diasRestantes(v));
      arr.push({
        id: `sub-vencida:${v.id}`,
        tipo: "error",
        mensaje: `Suscripción vencida de ${v.nombreCliente} hace ${dias} día${dias !== 1 ? "s" : ""} — ${store.config.moneda} ${v.total.toLocaleString()} en riesgo`,
        ventaId: v.id,
      });
    });
    return arr;
  }, [grupos, hayRecurrentes, store.config.moneda]);

  // Mapa rápido: leadId → sus suscripciones activas/por_vencer/vencidas
  const suscripcionesPorLead = useMemo(() => {
    if (!hayRecurrentes) return {};
    const map = {};
    ventasRecurrentes.forEach(v => {
      if (!v.leadId) return;
      if (!map[v.leadId]) map[v.leadId] = [];
      map[v.leadId].push({
        ...v,
        _estado: calcularEstadoSuscripcion(v),
        _diasRestantes: diasRestantes(v),
      });
    });
    return map;
  }, [ventasRecurrentes, hayRecurrentes]);

  return {
    hayRecurrentes,
    ventasRecurrentes,
    grupos,
    dinero,
    tasaRenovacion,
    alertasSuscripcion,
    suscripcionesPorLead,
  };
}
